
import {
  defineComponent, computed, ref, PropType, Ref,
} from '@vue/composition-api';
import { FCButton, FCSelect } from '@fc/angie-ui';
import { FormattedProduct } from '@fc/app-common/src/services/Shopify/types';
import { useCartContext } from '~/composables/useCartContext';
import FCImage from '~/components/FCImage.vue';
import WrapGloveSize from '~/components/v2/pdps/WrapGloveSize.vue';
import productsData from '~/data/index';
import { useAllIncludedModal } from '~/composables/useAllIncludedModal';
import {
  useUpsellItems,
} from '~/composables/upsellModal';
import { FullShopifyProductWithVariant } from '~/store/actions/shopify';
import { useMembershipPrice } from '~/composables/useMembershipPrice';

type ProductWithQuantity = FullShopifyProductWithVariant & {
  quantity: number;
};

interface UpsellProduct extends FormattedProduct {
  value?: string; // For v-model binding in FCSelect
  cta: string; // Call to action text
  isDisabled?: boolean; // To disable the button if already added
}

export default defineComponent({
  name: 'addToCartSuccessModal',
  components: {
    FCButton,
    FCSelect,
    FCImage,
    WrapGloveSize,
  },
  props: {
    productsAdded: {
      type: Object as PropType<Ref<string[]>>,
      required: true,
    },
    upsellProducts: {
      type: Array as PropType<UpsellProduct[]>,
      required: true,
    },
  },
  setup(props) {
    const { $store, $modal } = useNuxtApp();

    // Get full products and group by variant id with quantity
    const products = computed(() => {
      const fullProducts = $store.getters.cartProducts.shopifyItems.map((item: any) => $store.getters.fullProductWithSelectedVariant(item.variant.id));

      return Object.values(fullProducts.reduce((acc, item) => {
        const title = item.title?.toLowerCase() || '';
        const shouldExclude = title.includes('membership') || title.includes('subscription');

        if (shouldExclude) {
          return acc;
        }

        if (!acc[item.variant.id]) {
          acc[item.variant.id] = { ...item, quantity: 1 };
        } else {
          acc[item.variant.id].quantity += 1;
        }

        return acc;
      }, {}));
    }) as unknown as ProductWithQuantity[];

    const { upsellItems, updateUpsellItems } = useUpsellItems(ref(props.upsellProducts), products);

    // Get the subscription product that was filtered out
    const subscriptionProduct = computed(() => {
      const fullProducts = $store.getters.cartProducts.shopifyItems.map((item: any) => $store.getters.fullProductWithSelectedVariant(item.variant.id));

      return fullProducts.find((item: any) => {
        const title = item.title?.toLowerCase() || '';
        return title.includes('membership') || title.includes('subscription');
      });
    });

    const { insertItemToCart, summaryTotal } = useCartContext();
    const selectedItems = ref<string[]>([]);
    async function addToSelectedItems(id: string) {
      updateUpsellItems(id, 'Added!', true);
      selectedItems.value.push(id);

      await insertItemToCart(id);

      setTimeout(() => {
        updateUpsellItems(id, 'Add to Cart', false);
      }, 2000);
    }

    const closeAndToggle = () => {
      $modal.hide('addToCartSuccessModal');
      $store.dispatch('toggleDrawer');
    };

    function handleContinueFirstStep() {
      closeAndToggle();
    }

    const { openAllIncludedModal } = useAllIncludedModal();

    function openDetailsModal(productId: string) {
      const productDetails = computed(() => Object.values(productsData).find(prod => {
        const id = process.env.NUXT_ENV_IS_PROD === 'true' ? prod.prodShopifyId : prod.testShopifyId;
        return id === productId;
      }));

      if (!productDetails.value) {
        return;
      }

      openAllIncludedModal(productDetails.value);
    }

    const billingPlanKey = computed(() => $store.getters.membershipInCart?.metadata?.billingPlanKey);
    const { membershipPrice } = useMembershipPrice({
      billingPlanKey: billingPlanKey.value,
      defaultPrice: 3900,
    });

    return {
      products,
      subscriptionProduct,
      upsellItems,
      selectedItems,
      handleContinueFirstStep,
      total: computed(() => summaryTotal.value),
      addToSelectedItems,
      closeAndToggle,
      openDetailsModal,
      membershipPrice,
    };
  },
});
